/**
 * KEJSIK hero 3D viewer (three.js).
 *
 * Renders a GLB product model with studio reflections, neon rim lights, a pointer-
 * following pink light and a ring of sparkles. Rotation is driven by scroll progress
 * (setProgress) and pointer position (setPointer); the viewer eases towards both.
 *
 * Imported on demand by assets/kejsik.js only when a hero has a model configured.
 */

const THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.169.0/+esm';
const GLTF_URL = 'https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/loaders/GLTFLoader.js/+esm';
const ROOM_URL = 'https://cdn.jsdelivr.net/npm/three@0.169.0/examples/jsm/environments/RoomEnvironment.js/+esm';

/**
 * @param {HTMLElement} container - Element the canvas fills.
 * @param {{ url: string, reducedMotion?: boolean }} options
 */
export async function mount(container, { url, reducedMotion = false }) {
  const [THREE, { GLTFLoader }, { RoomEnvironment }] = await Promise.all([
    import(THREE_URL),
    import(GLTF_URL),
    import(ROOM_URL),
  ]);

  const coarse = window.matchMedia('(pointer: coarse)').matches;
  const dpr = window.devicePixelRatio || 1;
  // MSAA is wasted work on high-density screens, where pixels are already tiny.
  const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: dpr < 1.5, powerPreference: 'high-performance' });
  // Pixel ratio is the main GPU cost; start at 1.5 max and step down if frames run slow.
  let pixelRatio = Math.min(dpr, coarse ? 1.25 : 1.5);
  renderer.setPixelRatio(pixelRatio);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.82;
  renderer.domElement.setAttribute('aria-hidden', 'true');
  container.append(renderer.domElement);

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  scene.environment = envMap;

  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);
  camera.position.set(0, 0, 9);

  // Neon rim lights in the brand palette; the pink one follows the pointer.
  scene.add(new THREE.AmbientLight(0xffffff, 0.25));
  const key = new THREE.DirectionalLight(0xffffff, 1.4);
  key.position.set(2, 4, 6);
  const violet = new THREE.PointLight(0x7b3cff, 160, 30, 1.6);
  violet.position.set(-4, 1.5, 2);
  const blue = new THREE.PointLight(0x2f6bff, 140, 30, 1.6);
  blue.position.set(4.5, -1.5, 1.5);
  const pink = new THREE.PointLight(0xff4fd8, 90, 20, 1.8);
  pink.position.set(0, 0, 4);
  scene.add(key, violet, blue, pink);

  const gltf = await new GLTFLoader().loadAsync(url);
  const model = gltf.scene;

  // Sketchfab's root node already converts the model to Y-up; `upright` only fine-tunes the facing.
  const pivot = new THREE.Group();
  const upright = new THREE.Group();
  upright.rotation.set(0, Math.PI, 0);
  upright.add(model);
  pivot.add(upright);
  scene.add(pivot);

  const box = new THREE.Box3().setFromObject(upright);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  upright.position.sub(center);
  pivot.scale.setScalar(2.4 / Math.max(size.x, size.y, size.z));

  model.traverse((node) => {
    if (!node.isMesh) return;
    const old = node.material;
    const name = old.name || '';
    const c = old.color;
    const dark = c ? (c.r + c.g + c.b) / 3 < 0.1 : false;
    if (name.includes('Blue')) {
      // Blue trim (PS5 light strip) glows in the brand blue.
      old.emissive = new THREE.Color(0x2f6bff);
      old.emissiveIntensity = 1.6;
      old.envMapIntensity = 1.3;
    } else if (name.startsWith('Plastic') && !dark) {
      // Glossy, slightly lilac white plastic so the neon lights read on the surface.
      // Dark plastics (PS5 core) keep their colour.
      node.material = new THREE.MeshStandardMaterial({
        name,
        color: name === 'Plastic' ? 0xa9a2bb : 0xdcd8e6,
        roughness: 0.2,
        metalness: 0,
        roughnessMap: old.roughnessMap || null,
        normalMap: old.normalMap || null,
        envMapIntensity: 0.75,
        side: old.side,
      });
      old.dispose();
    } else if (old.isMeshStandardMaterial) {
      old.envMapIntensity = 1.3;
    }
  });

  // Sparkles: a loose shell of additive points around the case.
  const sparkleCount = reducedMotion || coarse ? 50 : 90;
  const positions = new Float32Array(sparkleCount * 3);
  const colors = new Float32Array(sparkleCount * 3);
  const palette = [new THREE.Color(0x7b3cff), new THREE.Color(0xff4fd8), new THREE.Color(0x2f6bff), new THREE.Color(0xf6f2ff)];
  for (let i = 0; i < sparkleCount; i++) {
    const r = 2.1 + Math.random() * 1.8;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions.set([r * Math.sin(phi) * Math.cos(theta), r * Math.cos(phi) * 0.75, r * Math.sin(phi) * Math.sin(theta)], i * 3);
    const c = palette[i % palette.length];
    colors.set([c.r, c.g, c.b], i * 3);
  }
  const sparkleGeo = new THREE.BufferGeometry();
  sparkleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  sparkleGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const dot = document.createElement('canvas');
  dot.width = dot.height = 64;
  const ctx = dot.getContext('2d');
  if (ctx) {
    const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.6)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 64, 64);
  }
  const dotTexture = new THREE.CanvasTexture(dot);
  const sparkleMat = new THREE.PointsMaterial({
    size: 0.09,
    map: dotTexture,
    vertexColors: true,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const sparkles = new THREE.Points(sparkleGeo, sparkleMat);
  scene.add(sparkles);

  // Resize to the container.
  const resize = () => {
    const { width, height } = container.getBoundingClientRect();
    if (!width || !height) return;
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
  };
  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(container);
  resize();

  // Animation state: targets set from outside, current values eased each frame.
  const target = { progress: 0, px: 0, py: 0 };
  const current = { progress: 0, px: 0, py: 0 };
  let visible = true;
  let frame = 0;
  const clock = new THREE.Clock();

  const visibility = new IntersectionObserver(([entry]) => {
    visible = Boolean(entry?.isIntersecting);
    if (visible && !frame) frame = requestAnimationFrame(tick);
  });
  visibility.observe(container);

  // Adaptive quality: average frame time over ~1 s, then lower resolution / drop sparkles.
  let lastTime = performance.now();
  let avgDelta = 16;
  let sampled = 0;
  const adapt = (now) => {
    const delta = Math.min(now - lastTime, 100);
    lastTime = now;
    avgDelta += (delta - avgDelta) * 0.05;
    if (++sampled < 60) return;
    sampled = 0;
    if (avgDelta > 22 && pixelRatio > 1) {
      pixelRatio = Math.max(1, pixelRatio - 0.25);
      renderer.setPixelRatio(pixelRatio);
      resize();
    } else if (avgDelta > 28 && sparkles.visible) {
      sparkles.visible = false;
    }
  };

  // Cap at ~60 fps: 120/144 Hz screens would otherwise render twice as many frames.
  let lastRender = 0;

  function tick(now = performance.now()) {
    frame = 0;
    if (!visible || document.hidden) {
      lastTime = performance.now();
      return;
    }
    if (now - lastRender < 12) {
      frame = requestAnimationFrame(tick);
      return;
    }
    lastRender = now;
    adapt(now);

    const t = clock.getElapsedTime();
    const ease = reducedMotion ? 1 : 0.07;
    for (const k of /** @type {const} */ (['progress', 'px', 'py'])) current[k] += (target[k] - current[k]) * ease;

    const p = current.progress;
    const idle = reducedMotion ? 0 : t * 0.35;
    // Scroll adds a full turn and tips the case forward mid-way so its top shows.
    pivot.rotation.y = -0.45 + idle + p * Math.PI * 2 + current.px * 0.5;
    pivot.rotation.x = 0.12 + Math.sin(p * Math.PI) * 0.55 + current.py * 0.3;
    pivot.rotation.z = Math.sin(p * Math.PI) * -0.18;
    pivot.position.y = reducedMotion ? 0 : Math.sin(t * 1.4) * 0.08;

    sparkles.rotation.y = -t * 0.08 - p * 1.5;
    sparkles.rotation.x = Math.sin(t * 0.2) * 0.1;

    pink.position.set(current.px * 4, -current.py * 3, 4);
    violet.intensity = 160 + Math.sin(t * 1.3) * 40;

    renderer.render(scene, camera);
    frame = requestAnimationFrame(tick);
  }
  frame = requestAnimationFrame(tick);

  const onVisibility = () => {
    if (!document.hidden && visible && !frame) frame = requestAnimationFrame(tick);
  };
  document.addEventListener('visibilitychange', onVisibility);

  return {
    /** @param {number} value 0–1 scroll progress through the hero */
    setProgress(value) {
      target.progress = value;
    },
    /** @param {number} x -1..1 @param {number} y -1..1 */
    setPointer(x, y) {
      target.px = x;
      target.py = y;
    },
    dispose() {
      cancelAnimationFrame(frame);
      frame = 0;
      visible = false;
      resizeObserver.disconnect();
      visibility.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      scene.traverse((node) => {
        if (node.geometry) node.geometry.dispose();
        if (node.material) [].concat(node.material).forEach((m) => m.dispose());
      });
      dotTexture.dispose();
      envMap.dispose();
      pmrem.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
